import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  isAxiosError,
} from "axios";
import { TOKEN_STORAGE_KEY } from "@/lib/constants";

const getToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_STORAGE_KEY);
};

const clearToken = (): void => {
  if (typeof window !== "undefined") {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }
};

/** Callback to run on 401 (e.g. clear auth and redirect). Set by app. */
let onUnauthorized: (() => void) | null = null;
export function setOnUnauthorized(callback: (() => void) | null) {
  onUnauthorized = callback;
}

const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    headers: { "Content-Type": "application/json" },
  });

  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isAxiosError(error) && error.response?.status === 401) {
        clearToken();
        onUnauthorized?.();
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

const platformBaseURL =
  import.meta.env.VITE_PLATFORM_URL || "http://localhost:8080";

export const platformApi = createAxiosInstance(platformBaseURL);

/** Unauthenticated instance for login/register. 401 here means bad
 *  credentials, not session expiry — do not trigger global logout. */
export const platformApiUnauth = axios.create({
  baseURL: platformBaseURL,
  headers: { "Content-Type": "application/json" },
});

export const engineApi = createAxiosInstance(
  import.meta.env.VITE_ENGINE_URL || "http://localhost:8081",
);

export const notificationsApi = createAxiosInstance(
  import.meta.env.VITE_NOTIFICATIONS_URL || "http://localhost:8084",
);
